// ============================================
// Prenta — useApi Hook
// ============================================
// Wraps auth token injection for authenticated API calls.
// Usage: const api = useApi();
//        await api.requests.create(body);
//        await api.makers.list();  // public endpoints work without token

import { useAuth } from './useAuth';
import { useCallback } from 'react';
import * as apiClient from '../utils/api';

export function useApi() {
  const { token, isAuthenticated } = useAuth();

  const getToken = useCallback(() => {
    if (!isAuthenticated) return null;
    return token;
  }, [isAuthenticated, token]);

  return {
    getToken,

    // Auth
    auth: {
      sync: () => apiClient.auth.sync(getToken()),
      me: () => apiClient.auth.me(getToken()),
    },

    // Makers (list/get are public)
    makers: {
      list: (params) => apiClient.makers.list(params),
      get: (id) => apiClient.makers.get(id),
      register: (body) => apiClient.makers.register(body, getToken()),
      update: (id, body) => apiClient.makers.update(id, body, getToken()),
    },

    // Services (list is public)
    services: {
      list: (params) => apiClient.services.list(params),
      create: (body) => apiClient.services.create(body, getToken()),
      update: (id, body) => apiClient.services.update(id, body, getToken()),
      remove: (id) => apiClient.services.remove(id, getToken()),
    },

    // Requests (list/get are public)
    requests: {
      list: (params) => apiClient.requests.list(params),
      get: (id) => apiClient.requests.get(id),
      create: (body) => apiClient.requests.create(body, getToken()),
      update: (id, body) => apiClient.requests.update(id, body, getToken()),
    },

    // Quotes
    quotes: {
      list: (requestId) => apiClient.quotes.list(requestId),
      send: (body) => apiClient.quotes.send(body, getToken()),
      respond: (id, body) => apiClient.quotes.respond(id, body, getToken()),
    },

    // Orders (all authenticated)
    orders: {
      list: (params) => apiClient.orders.list(params, getToken()),
      get: (id) => apiClient.orders.get(id, getToken()),
      updateStatus: (id, body) => apiClient.orders.updateStatus(id, body, getToken()),
    },

    // Reviews (list is public)
    reviews: {
      list: (makerId) => apiClient.reviews.list(makerId),
      create: (body) => apiClient.reviews.create(body, getToken()),
    },

    // Messages (all authenticated)
    messages: {
      list: (requestId) => apiClient.messages.list(requestId, getToken()),
      send: (body) => apiClient.messages.send(body, getToken()),
      markRead: (id) => apiClient.messages.markRead(id, getToken()),
      unreadCount: () => apiClient.messages.unreadCount(getToken()),
    },

    // Files
    files: {
      upload: (file) => apiClient.files.upload(file, getToken()),
      url: apiClient.files.url,
    },
  };
}
